import { useCallback, useEffect, useState } from "react";

import { AsyncTaskName, AsyncTaskStatusUpdateEventType } from "@performyard/service-contracts";

import { useAsyncTaskStream } from "../useAsyncTaskStream";

type UseUserAsyncTaskReturn = {
	task: AsyncTaskStatusUpdateEventType | null;
	isConnected: boolean;
	error: Error | null;
	isRetrying: boolean;
	triggerTask: (createTask: () => Promise<{ taskId: string }>) => Promise<void>;
	isInitializing: boolean;
};

/**
 * Hook to manage user-specific async tasks with automatic SSE connection.
 * Each time triggerTask is called, it creates a fresh task and connects to stream its updates.
 *
 * @returns Object containing task state, connection status, and trigger function
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const useUserAsyncTask = (taskName: AsyncTaskName): UseUserAsyncTaskReturn => {
	const [taskId, setTaskId] = useState<string | null>(null);
	const [isInitializing, setIsInitializing] = useState(false);

	const { task, isConnected, error, isRetrying } = useAsyncTaskStream(taskId, { scope: "user" });

	// Trigger a new task - always creates a fresh task
	const triggerTask = useCallback(async (createTask: () => Promise<{ taskId: string }>): Promise<void> => {
		// Clear old task and show initializing state
		setTaskId(null);
		setIsInitializing(true);

		try {
			// Create the new task
			const response = await createTask();

			// Connect to SSE for this task
			setTaskId(response.taskId);
		} catch (err) {
			// Reset state on error
			setIsInitializing(false);
			throw err;
		}
	}, []);

	// Stop showing initializing spinner once we get the first task update
	useEffect(() => {
		if (task && isInitializing) {
			setIsInitializing(false);
		}
	}, [task, isInitializing]);

	return {
		task,
		isConnected,
		error,
		isRetrying,
		triggerTask,
		isInitializing,
	};
};
